import { supabase } from './supabase.js';
import { GENEROS_TREE } from '../constants/musicConstants.js';
import { AI_SCAN_COST, GROOVES_PHONE_GATE_USER_MESSAGE, profileGroovesBalance } from '../marketplace/constants/groovesEconomy.js';

export { GROOVES_PHONE_GATE_USER_MESSAGE };

const FORMATO_OPTS = ['Vinilo 12"', 'Vinilo 7"', 'CD', 'Casete'];
const GENEROS_PRINCIPALES_LISTA = Object.keys(GENEROS_TREE).join(' | ');

const PAIS_ALIASES = {
  uk: 'Reino Unido',
  'u.k.': 'Reino Unido',
  'united kingdom': 'Reino Unido',
  england: 'Reino Unido',
  usa: 'Estados Unidos',
  us: 'Estados Unidos',
  'u.s.a.': 'Estados Unidos',
  'united states': 'Estados Unidos',
  spain: 'España',
  espana: 'España',
  españa: 'España',
  france: 'Francia',
  germany: 'Alemania',
  deutschland: 'Alemania',
  italy: 'Italia',
  japan: 'Japón',
  holland: 'Países Bajos',
  netherlands: 'Países Bajos',
  canada: 'Canadá',
  mexico: 'México',
  méxico: 'México',
  australia: 'Australia',
};

/** Sellos frecuentes para rescate desde texto OCR si el modelo no rellena `sello`. */
const SELLOS_CONOCIDOS = [
  'Columbia', 'Sony Music', 'Sony', 'Warner', 'Warner Bros', 'EMI', 'Universal', 'Polydor', 'Atlantic',
  'Decca', 'RCA', 'Virgin', 'Island', 'Capitol', 'Parlophone', 'Elektra', 'Geffen', 'Motown',
  'Mercury', 'Philips', 'BMG', 'Ariola', 'CBS', 'Epic', 'Harvest', 'Vertigo', 'Blue Note',
  'ECM', 'Nonesuch', 'Sub Pop', '4AD', 'Rough Trade', 'Domino', 'XL Recordings', 'Mute',
  'Hispavox', 'Zafiro', 'Discos Epic', 'Movieplay', 'Regal', 'La Voz De Su Amo',
];

/** Mensaje legible para errores de `analyzeImage`. */
export function iaScanErrorMessage(error) {
  if (!error) return null;
  if (error === 'api_key_missing') {
    return 'El análisis con IA no está configurado en esta versión de la app. Usa el modo manual o contacta con soporte.';
  }
  if (error === 'no_fields_detected') {
    return 'No se leyeron datos en la foto. Usa la ★ Portada con buena luz y el texto del disco visible.';
  }
  if (error === 'parse_error') {
    return 'No se pudo interpretar la respuesta de la IA. Prueba otra foto de la portada.';
  }
  if (String(error).startsWith('gemini_no_output')) {
    return 'La IA no devolvió resultados. Prueba con otra imagen (portada nítida, sin reflejos fuertes).';
  }
  if (error === 'gemini_error' || String(error).includes('quota')) {
    return 'El servicio de IA no está disponible ahora. Inténtalo en unos minutos o usa modo manual.';
  }
  return 'No se pudo completar el análisis. Revisa la foto de portada e inténtalo de nuevo.';
}

/** Concede Grooves vía Edge Function `award-grooves` (JWT del cliente). Errores silenciosos en logs opcional. */
export async function invokeAwardGrooves({ reason, refId, refType }) {
  const { data, error } = await supabase.functions.invoke('award-grooves', {
    body: { reason, ref_id: refId, ref_type: refType },
  });
  if (error) return { ok: false, data: null, error: error.message || 'invoke_failed' };
  if (data && data.ok === false) return { ok: false, data, error: data.message || data.error || 'award_failed' };
  return { ok: true, data, error: null };
}

/** Invoca Edge `gemini-vision` (clave GEMINI_API_KEY solo en Supabase). */
async function invokeGeminiVision(body) {
  const { data, error } = await supabase.functions.invoke('gemini-vision', { body });
  if (error) return { ok: false, gemini: null, error: 'invoke_failed' };
  if (!data?.ok) {
    const msg = data?.message || data?.error || 'gemini_error';
    return { ok: false, gemini: data?.gemini ?? null, error: msg };
  }
  return { ok: true, gemini: data.gemini, error: null };
}

export async function checkCanScan(userId) {
  if (!userId) return { canScan: false, grooves: 0, reason: 'no_auth' };
  const { data, error } = await supabase
    .from('profiles')
    .select('grooves, grooves_total, phone_verified_at')
    .eq('id', userId)
    .single();
  if (error || !data) return { canScan: false, grooves: 0, reason: 'error' };
  const grooves = profileGroovesBalance(data);
  if (!data.phone_verified_at) {
    return { canScan: false, grooves, reason: 'phone_not_verified' };
  }
  if (grooves < AI_SCAN_COST) return { canScan: false, grooves, reason: 'insufficient_grooves' };
  return { canScan: true, grooves, reason: null };
}

export async function consumeScan(userId) {
  if (!userId) return { ok: false, groovesRemaining: 0, error: 'no_auth' };
  const { data: sessionData } = await supabase.auth.getSession();
  const sessionUserId = sessionData?.session?.user?.id;
  if (!sessionUserId || sessionUserId !== userId) {
    return { ok: false, groovesRemaining: 0, error: 'no_auth' };
  }

  const { data: profPhone } = await supabase.from('profiles').select('phone_verified_at, grooves, grooves_total').eq('id', userId).maybeSingle();
  if (!profPhone?.phone_verified_at) {
    const g = profileGroovesBalance(profPhone);
    return { ok: false, groovesRemaining: Number.isFinite(g) ? g : 0, error: 'phone_not_verified' };
  }

  const { data, error } = await supabase.functions.invoke('consume-ai-scan', { body: {} });
  if (error) {
    const remaining = Number(data?.grooves_remaining);
    const msg = data?.message || error.message || 'invoke_failed';
    return {
      ok: false,
      groovesRemaining: Number.isFinite(remaining) ? remaining : 0,
      error: data?.error || msg,
    };
  }
  if (!data?.ok) {
    const remaining = Number(data?.grooves_remaining);
    return {
      ok: false,
      groovesRemaining: Number.isFinite(remaining) ? remaining : 0,
      error: data?.error || data?.message || 'consume_failed',
    };
  }
  const next = Number(data.grooves_remaining);
  return { ok: true, groovesRemaining: Number.isFinite(next) ? next : 0, error: null };
}

function normalizeTextField(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s || s.toLowerCase() === 'null') return null;
  return s;
}

function normalizeAnio(value) {
  const s = normalizeTextField(value);
  if (!s) return null;
  const m = s.match(/\b(18|19|20)\d{2}\b/);
  return m ? m[0] : /^\d{4}$/.test(s) ? s : null;
}

function firstNormalizedAnio(...candidates) {
  for (const c of candidates) {
    const y = normalizeAnio(c);
    if (y) return y;
  }
  return null;
}

function collectOcrBlob(parsed) {
  const t = parsed?.transcripcion_textos;
  const parts = [
    typeof t === 'string' ? t : null,
    t?.portada_inferior,
    t?.lomo,
    t?.parte_inferior,
    t?.otros,
    parsed?.texto_ocr,
    parsed?.transcripcion_texto,
    parsed?.texto_visible,
  ];
  return parts.filter(Boolean).join('\n');
}

/** Año: prioriza fecha de lanzamiento; si no, ©/℗ del texto OCR. */
function resolveAnio(parsed, ocrBlob) {
  const fromFields = firstNormalizedAnio(
    parsed?.anio,
    parsed?.fecha_lanzamiento,
    parsed?.fecha_de_lanzamiento,
    parsed?.release_year,
    parsed?.anio_lanzamiento,
    parsed?.anio_de_lanzamiento,
    parsed?.anio_copyright,
    parsed?.anio_impreso,
    parsed?.anio_edicion
  );
  if (fromFields) return fromFields;
  if (!ocrBlob) return null;

  const launch = ocrBlob.match(
    /(?:released|release date|fecha de lanzamiento|lanzamiento|original(?:ly)?|first (?:issued|released))[^0-9]{0,24}(\d{4})/i
  );
  if (launch) return launch[1];

  const copyright = ocrBlob.match(/(?:©|℗|copyright)\s*['"]?(\d{4})/i);
  if (copyright) return copyright[1];

  const years = [...ocrBlob.matchAll(/\b(19[5-9]\d|20[0-2]\d)\b/g)].map(m => m[1]);
  if (!years.length) return null;
  const unique = [...new Set(years)].map(Number).sort((a, b) => a - b);
  return String(unique[0]);
}

function resolveSello(parsed, ocrBlob) {
  const fromFields = normalizeTextField(
    parsed?.sello_discografico ??
      parsed?.sello ??
      parsed?.record_label ??
      parsed?.label ??
      parsed?.discografica
  );
  if (fromFields) return fromFields;
  if (!ocrBlob) return null;

  const afterCopyright = ocrBlob.match(
    /(?:©|℗)\s*\d{4}\s+([A-Za-z0-9][A-Za-z0-9\s&'.-]{2,48}?)(?:\s+(?:Records|Music|Ltd|Inc|GmbH|S\.A\.|S\.L\.))?/i
  );
  if (afterCopyright) {
    const name = normalizeTextField(afterCopyright[1]);
    if (name && name.length > 2) return name;
  }

  for (const label of SELLOS_CONOCIDOS) {
    if (new RegExp(`\\b${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(ocrBlob)) {
      return label;
    }
  }
  return null;
}

function resolvePais(parsed, ocrBlob) {
  const fromFields = normalizePais(
    parsed?.pais_edicion ?? parsed?.pais ?? parsed?.country ?? parsed?.pais_de_edicion
  );
  if (fromFields) return fromFields;
  if (!ocrBlob) return null;

  const madeIn = ocrBlob.match(
    /(?:made in|manufactured in|printed in|pressed in|hecho en|impreso en|fabricado en|editado en|distribuido en)[:\s]+([A-Za-zÁÉÍÓÚáéíóúñÑ\s-]{3,40})/i
  );
  if (madeIn) {
    const chunk = madeIn[1].trim().split(/[,.;|]/)[0];
    return normalizePais(chunk);
  }

  const pressed = ocrBlob.match(/\b(USA|U\.S\.A\.|UK|E\.U\.|EU)\b/i);
  if (pressed) {
    const token = pressed[1].toLowerCase().replace(/\./g, '');
    if (PAIS_ALIASES[token] || PAIS_ALIASES[pressed[1].toLowerCase()]) {
      return normalizePais(pressed[1]);
    }
  }
  return null;
}

function normalizePais(value) {
  const s = normalizeTextField(value);
  if (!s) return null;
  const key = s.toLowerCase().replace(/\./g, '');
  if (PAIS_ALIASES[key]) return PAIS_ALIASES[key];
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function normalizeFormato(value) {
  const s = normalizeTextField(value);
  if (!s) return null;
  const unified = s.replace(/[""]/g, '"');
  const exact = FORMATO_OPTS.find(o => o.toLowerCase() === unified.toLowerCase());
  if (exact) return exact;
  if (/vinilo.*12/i.test(unified)) return 'Vinilo 12"';
  if (/vinilo.*7/i.test(unified)) return 'Vinilo 7"';
  if (/^cd$/i.test(unified)) return 'CD';
  if (/casete/i.test(unified)) return 'Casete';
  return null;
}

function extractGeminiText(data) {
  const parts = data?.candidates?.[0]?.content?.parts ?? [];
  const fromParts = parts
    .filter(p => typeof p.text === 'string' && p.text.trim() && p.thought !== true)
    .map(p => p.text)
    .join('')
    .trim();
  if (fromParts) return fromParts;
  const legacy = data?.candidates?.[0]?.content?.parts?.[0]?.text;
  return typeof legacy === 'string' ? legacy.trim() : '';
}

function parseGeminiJsonObject(raw) {
  let clean = String(raw).replace(/```json|```/gi, '').trim();
  const start = clean.indexOf('{');
  const end = clean.lastIndexOf('}');
  if (start >= 0 && end > start) clean = clean.slice(start, end + 1);
  return JSON.parse(clean);
}

function fieldsFromParsed(parsed) {
  const ocrBlob = collectOcrBlob(parsed);
  const anio = resolveAnio(parsed, ocrBlob);
  const sello = resolveSello(parsed, ocrBlob);
  const pais = resolvePais(parsed, ocrBlob);

  return {
    artista: normalizeTextField(parsed.artista),
    titulo: normalizeTextField(parsed.titulo),
    anio,
    sello,
    pais,
    genero: normalizeTextField(parsed.genero),
    genero_principal: normalizeTextField(parsed.genero_principal),
    genero_sub: normalizeTextField(parsed.genero_sub),
    formato: normalizeFormato(parsed.formato),
  };
}

function hasDetectableFields(fields) {
  return Boolean(
    fields.artista ||
      fields.titulo ||
      fields.anio ||
      fields.sello ||
      fields.pais ||
      fields.genero ||
      fields.genero_principal ||
      fields.genero_sub ||
      fields.formato
  );
}

/** Cuenta campos útiles devueltos por la IA (para facturación y feedback). */
export function countAiScanFilledFields(fields) {
  if (!fields) return 0;
  let n = 0;
  if (String(fields.artista || '').trim()) n += 1;
  if (String(fields.titulo || '').trim()) n += 1;
  if (fields.anio) n += 1;
  if (String(fields.sello || '').trim()) n += 1;
  if (String(fields.pais || '').trim()) n += 1;
  if (String(fields.formato || '').trim()) n += 1;
  if (
    String(fields.genero || '').trim() ||
    String(fields.genero_principal || '').trim() ||
    String(fields.genero_sub || '').trim()
  ) {
    n += 1;
  }
  return n;
}

/**
 * Solo se cobran Grooves si la IA devolvió al menos título y artista (mínimo 2 campos útiles).
 */
export function aiScanQualifiesForBilling(fields) {
  if (!fields) return false;
  const hasArtista = Boolean(String(fields.artista || '').trim());
  const hasTitulo = Boolean(String(fields.titulo || '').trim());
  return hasArtista && hasTitulo && countAiScanFilledFields(fields) >= 2;
}

export function iaScanNoBillingMessage(fields) {
  if (aiScanQualifiesForBilling(fields)) return null;
  const n = countAiScanFilledFields(fields);
  if (n === 0) {
    return 'No se detectaron datos suficientes. No se han descontado Grooves.';
  }
  return 'Se rellenaron algunos datos, pero hace falta al menos título y artista para descontar Grooves.';
}

/**
 * Llama a Gemini Vision con la imagen y devuelve los campos detectados.
 * @param {string} base64 - imagen en base64 puro (sin prefijo data:)
 * @param {string} mediaType - ej: 'image/jpeg'
 * @returns {{ fields: object, lowConfidence: string[], error: string|null }}
 */
export async function analyzeImage(base64, mediaType = 'image/jpeg') {
  try {
    const out = await invokeGeminiVision({
      mode: 'analyze',
      base64,
      mediaType: mediaType || 'image/jpeg',
      generosPrincipalesLista: GENEROS_PRINCIPALES_LISTA,
    });

    if (!out.ok) {
      if (out.error === 'server_misconfigured' || out.error === 'invoke_failed') {
        return { fields: {}, lowConfidence: [], error: 'api_key_missing' };
      }
      const msg = String(out.error || '');
      return { fields: {}, lowConfidence: [], error: msg.includes('quota') ? msg : 'gemini_error' };
    }

    const data = out.gemini;
    const cand = data?.candidates?.[0];
    const raw = extractGeminiText(data);
    if (!raw) {
      const reason = cand?.finishReason || data?.promptFeedback?.blockReason || 'empty';
      if (process.env.NODE_ENV === 'development') {
        console.error('[analyzeImage] Sin texto en respuesta:', reason);
      }
      return { fields: {}, lowConfidence: [], error: `gemini_no_output:${reason}` };
    }

    let parsed;
    try {
      parsed = parseGeminiJsonObject(raw);
    } catch {
      if (process.env.NODE_ENV === 'development') {
        console.error('[analyzeImage] No se pudo parsear JSON');
      }
      return { fields: {}, lowConfidence: [], error: 'parse_error' };
    }

    const fields = fieldsFromParsed(parsed);
    if (!hasDetectableFields(fields)) {
      return { fields, lowConfidence: [], error: 'no_fields_detected' };
    }

    const lowConfidence = Array.isArray(parsed.confianza_baja) ? parsed.confianza_baja : [];

    return {
      fields,
      lowConfidence,
      error: null,
    };
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[analyzeImage] Error de red:', err);
    }
    return { fields: {}, lowConfidence: [], error: err.message || 'network_error' };
  }
}

export async function verifyIsDiskCover(base64, mediaType = 'image/jpeg') {
  try {
    const out = await invokeGeminiVision({
      mode: 'verify_cover',
      base64,
      mediaType: mediaType || 'image/jpeg',
    });
    if (!out.ok || !out.gemini) return { isValid: true, error: null };

    const raw = extractGeminiText(out.gemini).toLowerCase();
    if (raw.includes('false') && !raw.includes('true')) {
      return { isValid: false, error: null };
    }
    return { isValid: true, error: null };
  } catch {
    return { isValid: true, error: null };
  }
}
